import React, { useMemo, useState, useEffect, useRef } from 'react';
import './OrderViewModal.css';
import axios from "../../api/axios";
import NCALayerModal from "../../components/NCALayerModal";
import { ensureNCALayerConnected } from "../../utils/ensureNCALayer";

export default function OrderViewModal({
  orderDetails = {},
  onClose,
  locale = 'kz',
  profile,
  orderForm,
  setOrderForm,
  fetchOrderDetails,
  fetchGroups,
  token
}) {
  /* ================= i18n ================= */
  const t = useMemo(() => ({
    kz: {
      orderStatus: 'Күйі',
      status: {
        ready: "Дайын",
        pending: "Күтуде",
        signed: "Қол қойылған",
        rejected: "Қабылданбаған",
        draft: "Жоба",
        ready_signing: "Қол қоюға дайын",
        signing: "Қол қоюға дайындау",
      },
      applications: 'Өтінімдер:',
      signers: 'Қол қою реті:',
      downloadAgreement: 'Бұйрықты жүктеу',
      orderNumberLabel: 'Бұйрық нөмері:',
      orderDateLabel: 'Күні:',
      assignNumber: 'Нөмір қою',
      sign: 'ЭЦП-мен қол қою',
      signed: 'Қол қойылды',
      pending: 'Күтілуде',
      error: 'Қате орын алды',
      fileNotLoaded: '❌ PDF табылмады',
      signError: '❌ ЭЦП арқылы қол қою қате орындалды',
      signSuccess: '✅ ЭЦҚ сәтті қойылды!',
      noNCALayer: 'NCALayer табылмады. Бағдарламаны іске қосып, қайталап көріңіз.',
      you: 'Сіз',
      prepare: 'Қол қоюға дайындау',
      preparedOk: 'PDF дайын: қол қоюға болады',
      openPdf: 'PDF ашу',
    },
    ru: {
      orderStatus: 'Статус',
      status: {
        ready: "Готово",
        pending: "В ожидании",
        signed: "Подписано",
        rejected: "Отклонено",
        draft: "Черновик",
        ready_signing: "Готов к подписанию",
        signing: "Подготовка к подписи",
      },
      applications: 'Заявки:',
      signers: 'Порядок подписания:',
      downloadAgreement: 'Скачать приказ',
      orderNumberLabel: 'Номер приказа:',
      orderDateLabel: 'Дата:',
      assignNumber: 'Назначить номер',
      sign: 'Подписать ЭЦП',
      signed: 'Подписано',
      pending: 'Ожидается',
      error: 'Произошла ошибка',
      fileNotLoaded: '❌ PDF не найден',
      signError: '❌ Подпись ЭЦП выполнена неправильно',
      signSuccess: '✅ ЭЦП успешно поставлена!',
      noNCALayer: 'NCALayer не найден. Запустите приложение и повторите попытку.',
      you: 'Вы',
      prepare: 'Подготовить к подписи',
      preparedOk: 'PDF готов: можно подписывать',
      openPdf: 'Открыть PDF',
    },
  }[locale]), [locale]);

  /* ================= helpers ================= */
  const normalizeStatus = (s) => String(s || '').toLowerCase();

  function localizeServerMessage(msg, loc) {
    let m = String(msg || '');
    m = m.replace(/^❌\s*❌\s*/g, '❌ ');

    if (
      m.includes('қойылмаған') ||
      m.includes('номер') ||
      m.includes('қол қою кезеңіне дайын емес')
    ) {
      return loc === 'ru'
        ? '❌ Номер приказа не назначен или документ не подготовлен к подписи'
        : '❌ Бұйрық нөмірі қойылмаған немесе қол қою кезеңіне дайын емес';
    }

    return m || (loc === 'ru' ? '❌ Произошла ошибка' : '❌ Қате орын алды');
  }

  // Показ в UI: ДД.ММ.ГГГГ (без смещения по TZ для строк 'YYYY-MM-DD')
  function toDisplayDate(d) {
    if (!d) return '';
    if (typeof d === 'string') {
      const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const [, y, mm, dd] = m;
        return `${dd}.${mm}.${y}`;
      }
    }
    try {
      const dt = new Date(d);
      const dd = String(dt.getDate()).padStart(2, '0');
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const y = dt.getFullYear();
      return `${dd}.${mm}.${y}`;
    } catch {
      return '';
    }
  }

  // Значение для <input type="date">: YYYY-MM-DD
  function toInputDate(d) {
    if (!d) return '';
    if (typeof d === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
    }
    try {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      return `${y}-${mm}-${dd}`;
    } catch {
      return '';
    }
  }

  // ✅ Адаптер: берём функцию подписи из правильного места
  function getCreateCMSSignFn() {
    if (window.NCA?.createCMSSignatureFromBase64) return window.NCA.createCMSSignatureFromBase64;
    if (window.createCMSSignatureFromBase64) return window.createCMSSignatureFromBase64; // на всякий случай
    return null;
  }

  /* ================= state ================= */
  const [busyAssign, setBusyAssign] = useState(false);
  const [busySign, setBusySign] = useState(false);
  const [busyPrepare, setBusyPrepare] = useState(false);
  const [banner, setBanner] = useState(null);

  const [ncaModalOpen, setNcaModalOpen] = useState(false);
  const pendingActionRef = useRef(null); // "signOrder"

  const showBanner = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 4000);
  };

  /* ============ нормализация GET-данных ============ */
  const ord = orderDetails?.order ?? orderDetails ?? {};
  const orderId = ord.id ?? orderDetails?.order_id;
  const orderNumber = ord.order_number ?? orderDetails?.order_number ?? null;
  const orderStatus = ord.status ?? orderDetails?.status ?? 'draft';
  const statusKey = normalizeStatus(orderStatus);

  /* ============ номер + дата ============ */
  const [orderNumberView, setOrderNumberView] = useState(orderNumber);
  useEffect(() => setOrderNumberView(orderNumber ?? null), [orderNumber]);

  const [localOrderNumber, setLocalOrderNumber] = useState(orderForm?.order_number ?? orderNumber ?? '');
  useEffect(() => {
    setLocalOrderNumber(orderForm?.order_number ?? orderNumber ?? '');
  }, [orderNumber, orderForm?.order_number]);

  const orderDate = ord.order_registered_at ?? orderDetails?.order_registered_at ?? null;

  const [orderDateView, setOrderDateView] = useState(orderDate);
  useEffect(() => setOrderDateView(orderDate ?? null), [orderDate]);

  const [localOrderDate, setLocalOrderDate] = useState(orderForm?.order_registered_at ?? orderDate ?? '');
  useEffect(() => {
    setLocalOrderDate(orderForm?.order_registered_at ?? orderDate ?? '');
  }, [orderDate, orderForm?.order_registered_at]);

  /* ============ роль/переводы ============ */
  const lang = (locale || localStorage.getItem('lang') || 'kz').startsWith('ru') ? 'ru' : 'kz';

  const roleTranslations = {
    internal_expert: { kz: 'Ішкі сарапшы', ru: 'internal_expert' },
    department_head: { kz: 'Бөлім басшысы', ru: 'department_supervisor' },
    deputy_director: { kz: 'Директордың орынбасары', ru: 'Заместитель директора' },
    registrar: { kz: 'Тіркеуші', ru: 'moderator_registrar' },
    director: { kz: 'Директор', ru: 'Директор' },
  };

  const trRole = (k) => roleTranslations[String(k || '').toLowerCase()]?.[lang] || k || '—';

  const isRegistrar = useMemo(() => {
    const key = (profile?.role?.key || profile?.role?.name || '').toLowerCase();
    return key.includes('registrar') || key.includes('тіркеуш') || key.includes('moderator_registrar');
  }, [profile]);

  const isInternalExpert = useMemo(() => {
    const key = (profile?.role?.key || profile?.role?.name || '').toLowerCase();
    return key.includes('internal_expert') || key.includes('ішкі сарапшы') || key.includes('internal_expert');
  }, [profile]);

  /* ============ вычисления для UI ============ */
  const statusColor = ({
    ready: '#2e7d32',
    pending: '#e67e22',
    signed: '#2e7d32',
    rejected: '#c0392b',
    draft: '#7f8c8d',
    ready_signing: '#2563eb',
    signing: '#2563eb',
  }[statusKey]) || '#7f8c8d';

  const isPrepared =
    Boolean(ord?.pdf_ready || ord?.converted_at || ord?.pdf_url || ord?.pdf_path) ||
    statusKey === 'signing';

  const canPrepare =
    isInternalExpert &&
    !!orderNumberView &&
    statusKey === 'ready_signing';

  const canAssignNumber = isRegistrar && !orderNumberView;

  const canSign = useMemo(() => {
    if (!profile || !(orderDetails?.signers?.length)) return false;
    const me = orderDetails.signers.find(s => s.user_id === profile.id);
    if (!me || me.signed) return false;

    if (isRegistrar && !orderNumberView) return false;
    if (isInternalExpert && !isPrepared) return false;

    return true;
  }, [profile, orderDetails, isRegistrar, isInternalExpert, orderNumberView, isPrepared]);

  /* ================= actions ================= */

  const handleAssignNumber = async () => {
    if (!orderId) return;

    const num = String(localOrderNumber || '').trim();
    const d = toInputDate(localOrderDate);

    if (!num || !d) return;

    try {
      setBusyAssign(true);

      await axios.post(
        `/orders/${orderId}/assign-number`,
        { order_number: num, order_date: d },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOrderNumberView(num);
      setOrderDateView(d);
      setOrderForm?.({ ...(orderForm || {}), order_number: num, order_registered_at: d });

      showBanner(
        'success',
        locale === 'ru'
          ? '✅ Номер и дата приказа назначены, Word-файл обновлён'
          : '✅ Бұйрық нөмірі мен күні енгізілді, Word файлы жаңартылды'
      );

      await fetchOrderDetails?.(orderId);
      await fetchGroups?.();
    } catch (err) {
      showBanner('error', `❌ ${err?.response?.data?.message || err?.message || t.error}`);
    } finally {
      setBusyAssign(false);
    }
  };

  const handlePrepareToSign = async () => {
    if (!orderId) return;

    if (!orderNumberView || statusKey !== 'ready_signing') {
      showBanner('error', localizeServerMessage('❌ Приказ нөмірі қойылмаған немесе қол қою кезеңіне дайын емес', locale));
      return;
    }

    try {
      setBusyPrepare(true);

      await axios.get(`/orders/${orderId}/convert-pdf`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });

      showBanner('success', t.preparedOk);
      await fetchOrderDetails?.(orderId);
    } catch (err) {
      const raw = err?.response?.data?.message || err?.message;
      showBanner('error', localizeServerMessage(raw, locale));
    } finally {
      setBusyPrepare(false);
    }
  };

  const signOrderFlow = async () => {
    if (!orderId) return;

    const createSign = getCreateCMSSignFn();
    if (!createSign) {
      showBanner('error', t.noNCALayer);
      return;
    }

    setBusySign(true);

    try {
      const pdfRes = await axios.get(
        `/orders/${orderId}/pdf-base64`,
        { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } }
      );

      const base64File = pdfRes?.data?.base64;
      if (!base64File) {
        showBanner('error', t.fileNotLoaded);
        return;
      }

      window.blockScreen?.();

      createSign(
        "PKCS12",
        "SIGNATURE",
        base64File,
        true,
        async (result) => {
          window.unblockScreen?.();

          const code = result?.getCode ? result.getCode() : result?.code;
          const ok = String(code) === "200";

          if (!ok) {
            showBanner('error', t.signError);
            setBusySign(false);
            return;
          }

          try {
            const signedPdfBase64 = result?.getResponseObject
              ? result.getResponseObject()
              : result?.responseObject;

            await axios.post(
              `/orders/${orderId}/sign`,
              { signed_pdf_base64: signedPdfBase64 },
              {
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            showBanner('success', t.signSuccess);

            await fetchOrderDetails?.(orderId);
            await fetchGroups?.();
          } catch (err) {
            const msg = err?.response?.data?.message || err?.message || t.error;
            showBanner('error', `❌ ${msg}`);
          } finally {
            setBusySign(false);
          }
        }
      );
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || t.error;
      showBanner('error', `❌ ${msg}`);
      setBusySign(false);
    } finally {
      window.unblockScreen?.();
    }
  };

  const handleSignOrder = async () => {
    if (!orderId) return;

    const ok = await ensureNCALayerConnected({ connectTimeoutMs: 1200 });
    if (!ok) {
      pendingActionRef.current = "signOrder";
      setNcaModalOpen(true);
      return;
    }

    await signOrderFlow();
  };

  const handleOpenPdf = async () => {
    if (!orderId) return;

    const pdfUrl =
      ord?.pdf_url ||
      (ord?.pdf_path
        ? `https://expertise.gov.kz/storage/${String(ord.pdf_path).replace('public/', '')}`
        : null);

    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const pdfRes = await axios.get(`/orders/${orderId}/pdf-base64`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const base64 = pdfRes?.data?.base64;
      if (!base64) {
        showBanner('error', t.fileNotLoaded);
        return;
      }

      const byteChars = atob(base64);
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
      const byteArray = new Uint8Array(byteNums);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const raw = err?.response?.data?.message || err?.message;
      showBanner('error', localizeServerMessage(raw, locale));
    }
  };

  const handleDownloadAgreement = async () => {
    if (!orderId) return;

    try {
      const res = await axios.get(`/orders/${orderId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const num = orderNumberView ? `-${orderNumberView}` : '';
      link.setAttribute('download', `order-${orderId}${num}.docx`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let msg = err?.response?.data?.message || err?.message || t.error;

      if (msg.includes('қойылмаған') || msg.includes('номер') || msg.includes('қол қою кезеңіне дайын емес')) {
        msg = locale === 'ru'
          ? '❌ Номер приказа не назначен или документ не подготовлен к подписи'
          : '❌ Бұйрық нөмірі қойылмаған немесе қол қою кезеңіне дайын емес';
      }

      showBanner('error', msg);
    }
  };

  /* ================= render ================= */
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 1000, fontFamily: 'Segoe UI, sans-serif'
    }}>
      <div style={{
        backgroundColor: '#ffffff', width: '640px', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#003366', color: 'white', padding: '16px 24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            {ord?.title || '—'}
          </h2>
          <button onClick={onClose} style={{
            fontSize: '22px', background: 'none', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold'
          }}>×</button>
        </div>

        {banner && (
          <div style={{
            padding: '10px 16px',
            backgroundColor: banner.type === 'success' ? '#e8f5e9' : '#fdecea',
            color: banner.type === 'success' ? '#1b5e20' : '#c62828',
            borderBottom: '1px solid #eee'
          }}>
            {banner.text}
          </div>
        )}

        {/* Info */}
        <div style={{ padding: '20px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #ccc' }}>
          <p><strong>{t.orderNumberLabel}</strong> {orderNumberView || '—'}</p>
          <p><strong>{t.orderDateLabel}</strong> {orderDateView ? toDisplayDate(orderDateView) : '—'}</p>
          <p>
            <strong>{t.orderStatus}:</strong>{' '}
            <span style={{ color: statusColor, fontWeight: 600 }}>
              {t.status?.[statusKey] || orderStatus || '—'}
            </span>
          </p>
        </div>

        {/* Applications + Signers */}
        <div style={{ display: 'flex', padding: '20px', gap: '24px', flexWrap: 'wrap', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ flex: '1 1 280px' }}>
            <h4 style={{ margin: '0 0 10px', color: '#003366' }}>{t.applications}</h4>
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {((orderDetails?.application_groups ?? [])).flatMap((g) =>
                (g.applications ?? []).map((a) => ({
                  key: `${g.id}-${a.id ?? a.title}`,
                  text: `${g.title} — ${a.title ?? ''}`.trim()
                }))
              ).map(item => (
                <li key={item.key} style={{ marginBottom: '6px', fontSize: '14px' }}>📘 {item.text}</li>
              ))}
            </ul>
          </div>

          <div style={{ flex: '1 1 280px' }}>
            <h4 style={{ margin: '0 0 10px', color: '#003366' }}>{t.signers}</h4>
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {(orderDetails?.signers || []).map((s, i) => {
                const me = s.user_id === profile?.id;
                return (
                  <li
                    key={i}
                    style={{
                      marginBottom: '6px',
                      fontSize: '14px',
                      color: s.signed ? '#228B22' : '#c0392b'
                    }}
                  >
                    {trRole(s.role)}{me ? ` (${t.you})` : ''}: {s.signed ? t.signed : t.pending}
                    {s.signed_at && <span style={{ color: '#555', marginLeft: 6 }}>({s.signed_at})</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Registrar Assign */}
        {canAssignNumber && (
          <div style={{ padding: '20px', borderBottom: '1px solid #e0e0e0' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              {t.orderNumberLabel}
            </label>

            <input
              type="text"
              value={localOrderNumber}
              onChange={e => setLocalOrderNumber(e.target.value)}
              placeholder={locale === 'kz' ? 'Нөмір енгізіңіз' : 'Введите номер'}
              style={{
                width: '100%', padding: '10px', border: '1px solid #ccc',
                borderRadius: '4px', marginBottom: '12px', fontSize: '14px'
              }}
            />

            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              {t.orderDateLabel}
            </label>

            <input
              type="date"
              value={toInputDate(localOrderDate)}
              onChange={e => setLocalOrderDate(e.target.value)}
              style={{
                width: '100%', padding: '10px', border: '1px solid #ccc',
                borderRadius: '4px', marginBottom: '12px', fontSize: '14px'
              }}
            />

            <button
              onClick={handleAssignNumber}
              disabled={busyAssign || !String(localOrderNumber || '').trim() || !toInputDate(localOrderDate)}
              style={{
                padding: '10px 16px', backgroundColor: '#003366', color: 'white',
                border: 'none', borderRadius: '4px', fontWeight: '600',
                opacity: (busyAssign || !String(localOrderNumber || '').trim() || !toInputDate(localOrderDate)) ? 0.7 : 1,
                cursor: (busyAssign || !String(localOrderNumber || '').trim() || !toInputDate(localOrderDate)) ? 'not-allowed' : 'pointer'
              }}
            >
              {busyAssign ? '…' : t.assignNumber}
            </button>
          </div>
        )}

        <NCALayerModal
          open={ncaModalOpen}
          locale={locale}
          onClose={() => {
            setNcaModalOpen(false);
            pendingActionRef.current = null;
          }}
          onRetry={async () => {
            const ok = await ensureNCALayerConnected({ connectTimeoutMs: 1200 });
            if (!ok) return;

            setNcaModalOpen(false);

            const action = pendingActionRef.current;
            pendingActionRef.current = null;

            if (action === "signOrder") {
              await signOrderFlow();
            }
          }}
        />

        {/* Bottom Buttons */}
        <div style={{ padding: '20px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            onClick={handleDownloadAgreement}
            style={{
              padding: '10px 16px',
              backgroundColor: '#fff',
              color: '#003366',
              border: '2px solid #003366',
              borderRadius: '4px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            📄 {t.downloadAgreement}
          </button>

          <button
            onClick={handleOpenPdf}
            style={{
              padding: '10px 16px',
              backgroundColor: '#e9f2ff',
              color: '#0b3da8',
              border: '2px solid #bcd3ff',
              borderRadius: '4px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            🖨️ {t.openPdf}
          </button>

          {canPrepare && (
            <button
              onClick={busyPrepare ? undefined : handlePrepareToSign}
              disabled={busyPrepare}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontWeight: '600',
                cursor: busyPrepare ? 'not-allowed' : 'pointer',
                opacity: busyPrepare ? 0.7 : 1
              }}
            >
              {busyPrepare ? '…' : t.prepare}
            </button>
          )}

          {canSign && (
            <button
              onClick={busySign ? undefined : handleSignOrder}
              disabled={busySign}
              style={{
                padding: '10px 20px',
                backgroundColor: '#003366',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontWeight: '600',
                cursor: busySign ? 'not-allowed' : 'pointer',
                opacity: busySign ? 0.7 : 1
              }}
            >
              {busySign ? '…' : `✍️ ${t.sign}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
